/**
 * SharePoint File Browser — lets customers browse their SharePoint document libraries
 * and import files directly into the AI training pipeline.
 *
 * Auth: uses the site's Microsoft 365 admin consent connection (MicrosoftGraphConfig).
 * No credentials needed from the customer beyond the one-time M365 connect.
 *
 * Data path:
 *   1. Browse: admin API → Graph API (Sites → Drives → Files)
 *   2. Import: admin API downloads file from Graph → returns bytes →
 *              browser POSTs bytes to scraper /upload endpoint
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TODO (Jerome / scraper team): SCHEDULED DIFFERENTIAL SYNC
 * ─────────────────────────────────────────────────────────────────────────────
 * Currently this is MANUAL ONLY — a user must open this browser and click Import.
 * We need automatic, scheduled, differential ingestion from SharePoint folders.
 *
 * What needs to be built:
 *   1. "Watch this folder" config — customer selects a site/drive/folder and saves
 *      it as a watched source (stored in ScraperWatchedSources table or similar).
 *   2. Scheduler job (Hangfire or Azure Function timer) — runs every N hours,
 *      calls MicrosoftGraph/Files for each watched source, compares lastModified
 *      against what was last ingested, imports only NEW or CHANGED files.
 *   3. Differential tracking — store (siteId, driveId, itemId, lastImportedAt,
 *      lastModified) per file. On each run: skip if lastModified <= lastImportedAt.
 *   4. UI surface here — show "Auto-sync: every 24h | Last synced: 2h ago | Next: 22h"
 *      with a manual "Sync Now" button to force an immediate re-check.
 *
 * Hook-in point for the scheduler:
 *   - Backend: create ISharePointSyncService, register in Startup, call from
 *     a RecurringJob.AddOrUpdate("sharepoint-sync", ...) in Startup.cs alongside
 *     other Hangfire jobs.
 *   - The importSelected() function in this file shows the per-file download+upload
 *     pattern — the backend service should replicate the same two steps server-side
 *     (Graph download → POST to scraper /upload) without needing a browser.
 *   - Admin API already has MicrosoftGraph/DownloadFile — reuse that internally.
 *
 * Until this is built, the "Refresh" button below re-reads the current folder
 * so at least users can see new/changed files without navigating away and back.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import React, { useState, useCallback, useEffect } from "react";
import useApi from "@/hooks/useApi";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import { Link } from "react-router-dom";

const SUPPORTED_EXTENSIONS = new Set([
  "pdf", "docx", "doc", "xlsx", "xls", "csv", "txt", "md", "pptx", "ppt",
  "jpg", "jpeg", "png"
]);

function fileExtension(name: string): string {
  return name.split(".").pop()?.toLowerCase() ?? "";
}

function isSupported(name: string): boolean {
  return SUPPORTED_EXTENSIONS.has(fileExtension(name));
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString();
}

function fileIcon(name: string, isFolder: boolean): string {
  if (isFolder) return "📁";
  const ext = fileExtension(name);
  if (ext === "pdf") return "📄";
  if (["docx", "doc"].includes(ext)) return "📝";
  if (["xlsx", "xls", "csv"].includes(ext)) return "📊";
  if (["pptx", "ppt"].includes(ext)) return "📑";
  if (["jpg", "jpeg", "png"].includes(ext)) return "🖼";
  return "📃";
}

interface SpSite {
  id: string;
  displayName: string;
  webUrl: string;
}

interface SpDrive {
  id: string;
  name: string;
  driveType: string;
  webUrl: string;
}

interface SpItem {
  id: string;
  name: string;
  isFolder: boolean;
  mimeType: string;
  size: number;
  lastModified: string;
  webUrl: string;
}

interface BreadcrumbEntry {
  id: string | null;  // null = root of drive
  name: string;
}

interface Props {
  scraperApiUrl: string;
  scraperHeaders: Record<string, string>;
  onImportComplete: () => void;
}

export default function SharePointFileBrowser({ scraperApiUrl, scraperHeaders, onImportComplete }: Props) {
  const api = useApi();

  const [sites, setSites] = useState<SpSite[]>([]);
  const [drives, setDrives] = useState<SpDrive[]>([]);
  const [items, setItems] = useState<SpItem[]>([]);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbEntry[]>([]);

  const [selectedSite, setSelectedSite] = useState<SpSite | null>(null);
  const [selectedDrive, setSelectedDrive] = useState<SpDrive | null>(null);

  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ done: number; total: number } | null>(null);
  const [importLog, setImportLog] = useState<{ name: string; ok: boolean; msg: string }[]>([]);

  const [loadingSites, setLoadingSites] = useState(true);
  const [loadingDrives, setLoadingDrives] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load sites on mount
  useEffect(() => {
    setLoadingSites(true);
    api.admin.get("MicrosoftGraph/Sites")
      .then(r => r.json())
      .then(data => setSites(Array.isArray(data) ? data : []))
      .catch(() => setSites([]))
      .finally(() => setLoadingSites(false));
  }, [api]);

  // Load drives when site is selected
  const selectSite = useCallback(async (site: SpSite) => {
    setSelectedSite(site);
    setSelectedDrive(null);
    setItems([]);
    setBreadcrumb([]);
    setCheckedIds(new Set());
    setError(null);
    setLoadingDrives(true);
    try {
      const res = await api.admin.get(`MicrosoftGraph/Drives?graphSiteId=${encodeURIComponent(site.id)}`);
      const data = await res.json();
      setDrives(Array.isArray(data) ? data : []);
    } catch {
      setError("Failed to load document libraries.");
      setDrives([]);
    } finally {
      setLoadingDrives(false);
    }
  }, [api]);

  // Load files in a drive (at root or folder)
  const browseFolder = useCallback(async (drive: SpDrive, folderId: string | null, folderName: string) => {
    setSelectedDrive(drive);
    setCheckedIds(new Set());
    setError(null);
    setLoadingFiles(true);

    // Update breadcrumb
    if (folderId === null) {
      // Going to drive root — reset breadcrumb to just the drive
      setBreadcrumb([{ id: null, name: drive.name }]);
    } else {
      // Going into a subfolder — append
      setBreadcrumb(prev => {
        const idx = prev.findIndex(b => b.id === folderId);
        if (idx >= 0) return prev.slice(0, idx + 1);
        return [...prev, { id: folderId, name: folderName }];
      });
    }

    try {
      const params = `driveId=${encodeURIComponent(drive.id)}${folderId ? `&folderId=${encodeURIComponent(folderId)}` : ""}`;
      const res = await api.admin.get(`MicrosoftGraph/Files?${params}`);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setError("Failed to load files.");
      setItems([]);
    } finally {
      setLoadingFiles(false);
    }
  }, [api]);

  // Navigate to a breadcrumb level
  const navigateBreadcrumb = useCallback((entry: BreadcrumbEntry) => {
    if (!selectedDrive) return;
    browseFolder(selectedDrive, entry.id, entry.name);
  }, [selectedDrive, browseFolder]);

  // Refresh: re-read the current folder (picks up files added/changed since last load)
  const refreshCurrentFolder = useCallback(() => {
    if (!selectedDrive) return;
    const current = breadcrumb[breadcrumb.length - 1] ?? null;
    browseFolder(selectedDrive, current?.id ?? null, current?.name ?? selectedDrive.name);
  }, [selectedDrive, breadcrumb, browseFolder]);

  // Toggle file selection
  function toggleCheck(id: string) {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Import selected files
  async function importSelected() {
    if (!selectedDrive) return;
    const toImport = items.filter(f => !f.isFolder && checkedIds.has(f.id));
    if (toImport.length === 0) return;

    setImporting(true);
    setImportLog([]);
    setImportProgress({ done: 0, total: toImport.length });

    for (let i = 0; i < toImport.length; i++) {
      const file = toImport[i];
      try {
        // Step 1: Download from SharePoint via admin (uses Graph token)
        const dlUrl = `/api/admin/MicrosoftGraph/DownloadFile?driveId=${encodeURIComponent(selectedDrive.id)}&itemId=${encodeURIComponent(file.id)}`;
        const dlResp = await fetch(dlUrl, { credentials: "include" });
        if (!dlResp.ok) throw new Error(`Download failed (${dlResp.status})`);

        const blob = await dlResp.blob();
        const fileObj = new File([blob], file.name, { type: blob.type || "application/octet-stream" });

        // Step 2: Upload to scraper service (same path as File Training)
        const formData = new FormData();
        formData.append("file", fileObj);
        formData.append("name", file.name);

        const uploadResp = await fetch(`${scraperApiUrl}/upload`, {
          method: "POST",
          headers: scraperHeaders,
          body: formData
        });

        if (uploadResp.ok) {
          setImportLog(prev => [...prev, { name: file.name, ok: true, msg: "Imported" }]);
        } else {
          const err = await uploadResp.json().catch(() => ({}));
          throw new Error(err.detail || `Upload failed (${uploadResp.status})`);
        }
      } catch (ex: any) {
        setImportLog(prev => [...prev, { name: file.name, ok: false, msg: ex.message ?? "Failed" }]);
      }
      setImportProgress({ done: i + 1, total: toImport.length });
    }

    setImporting(false);
    setCheckedIds(new Set());
    const anySuccess = importLog.some(l => l.ok) || toImport.length > 0;
    if (anySuccess) onImportComplete();
  }

  const checkedFiles = items.filter(f => !f.isFolder && checkedIds.has(f.id));

  // ── Render ────────────────────────────────────────────────────────────────

  if (loadingSites) {
    return <div className="py-8 text-sm text-slate-400 text-center">Loading your SharePoint sites…</div>;
  }

  if (sites.length === 0) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
        <p className="font-medium mb-1">No SharePoint sites found</p>
        <p className="text-xs mb-3">
          Make sure your Microsoft 365 connection is active and your account has access to at least one SharePoint site.
          The app needs <strong>Sites.ReadWrite.All</strong> permission.
        </p>
        <Link to="/integrations/microsoft-365" className="text-xs text-blue-700 underline">
          Check Microsoft 365 connection →
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* Site selector */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-slate-700 flex-shrink-0">Site</label>
        <select
          className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
          value={selectedSite?.id ?? ""}
          onChange={e => {
            const s = sites.find(x => x.id === e.target.value);
            if (s) selectSite(s);
          }}
        >
          <option value="">Select a SharePoint site…</option>
          {sites.map(s => (
            <option key={s.id} value={s.id}>{s.displayName || s.webUrl}</option>
          ))}
        </select>
      </div>

      {/* Library selector */}
      {selectedSite && (
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-slate-700 flex-shrink-0">Library</label>
          {loadingDrives ? (
            <span className="text-sm text-slate-400">Loading…</span>
          ) : (
            <select
              className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={selectedDrive?.id ?? ""}
              onChange={e => {
                const d = drives.find(x => x.id === e.target.value);
                if (d) browseFolder(d, null, d.name);
              }}
            >
              <option value="">Select a document library…</option>
              {drives.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Breadcrumb + Refresh */}
      {breadcrumb.length > 0 && (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 text-xs text-slate-500 flex-wrap min-w-0">
            {breadcrumb.map((crumb, i) => (
              <React.Fragment key={crumb.id ?? "root"}>
                {i > 0 && <span className="text-slate-300">/</span>}
                <button
                  className={`hover:text-blue-600 ${i === breadcrumb.length - 1 ? "font-medium text-slate-700" : ""}`}
                  onClick={() => navigateBreadcrumb(crumb)}
                >
                  {crumb.name}
                </button>
              </React.Fragment>
            ))}
          </div>
          {/* Manual refresh — re-reads this folder to pick up new/changed files.
              TODO Jerome: replace this with auto scheduled sync (see top of file). */}
          <button
            type="button"
            className="flex-shrink-0 text-xs text-blue-600 hover:text-blue-800 border border-blue-200 rounded px-2 py-0.5 hover:bg-blue-50 disabled:opacity-40"
            onClick={refreshCurrentFolder}
            disabled={loadingFiles}
            title="Re-read this folder to pick up files added or changed since last load"
          >
            {loadingFiles ? "Refreshing…" : "↻ Refresh"}
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="text-sm text-red-600">{error}</div>
      )}

      {/* File list */}
      {selectedDrive && (
        loadingFiles ? (
          <div className="py-6 text-sm text-slate-400 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-6 text-sm text-slate-400 text-center">This folder is empty.</div>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-0 text-xs font-semibold text-slate-400 uppercase tracking-wide bg-slate-50 border-b border-slate-200 px-3 py-2">
              <span className="w-5"></span>
              <span className="pl-2">Name</span>
              <span className="w-20 text-right pr-3">Size</span>
              <span className="w-24 text-right pr-3">Modified</span>
              <span className="w-8"></span>
            </div>

            <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
              {items.map(item => {
                const supported = !item.isFolder && isSupported(item.name);
                const checked   = checkedIds.has(item.id);
                return (
                  <div
                    key={item.id}
                    className={`grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-0 px-3 py-2 text-sm ${item.isFolder ? "hover:bg-slate-50 cursor-pointer" : supported ? "hover:bg-blue-50" : "opacity-50"}`}
                    onClick={() => {
                      if (item.isFolder && selectedDrive) {
                        browseFolder(selectedDrive, item.id, item.name);
                      }
                    }}
                  >
                    {/* Checkbox (files only) */}
                    <div className="w-5 flex items-center" onClick={e => e.stopPropagation()}>
                      {!item.isFolder && supported && (
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCheck(item.id)}
                          className="accent-blue-500"
                          aria-label={`Select ${item.name}`}
                        />
                      )}
                    </div>

                    {/* Icon + Name */}
                    <div className="pl-2 flex items-center gap-2 min-w-0">
                      <span className="text-base flex-shrink-0">{fileIcon(item.name, item.isFolder)}</span>
                      <span className={`truncate ${item.isFolder ? "font-medium text-slate-700" : supported ? "text-slate-700" : "text-slate-400"}`}>
                        {item.name}
                      </span>
                      {!item.isFolder && !supported && (
                        <span className="text-xs text-slate-300 flex-shrink-0">unsupported</span>
                      )}
                    </div>

                    {/* Size */}
                    <div className="w-20 text-right pr-3 text-slate-400 text-xs">
                      {!item.isFolder ? formatBytes(item.size) : ""}
                    </div>

                    {/* Modified */}
                    <div className="w-24 text-right pr-3 text-slate-400 text-xs">
                      {formatDate(item.lastModified)}
                    </div>

                    {/* Folder arrow */}
                    <div className="w-8 text-slate-300 text-xs text-right">
                      {item.isFolder ? "›" : ""}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer: selection count + import */}
            <div className="border-t border-slate-100 px-3 py-2 flex items-center justify-between bg-slate-50">
              <span className="text-xs text-slate-500">
                {checkedFiles.length === 0
                  ? `${items.filter(i => !i.isFolder && isSupported(i.name)).length} importable file(s) — check to select`
                  : `${checkedFiles.length} file(s) selected`}
              </span>
              <div className="flex gap-2">
                {checkedFiles.length > 0 && (
                  <SecondaryBlueButton
                    label="Clear"
                    onClick={() => setCheckedIds(new Set())}
                    disabled={importing}
                  />
                )}
                <PrimaryBlueButton
                  label={importing ? `Importing ${importProgress?.done}/${importProgress?.total}…` : `Import ${checkedFiles.length || ""} File(s)`}
                  onClick={importSelected}
                  disabled={importing || checkedFiles.length === 0}
                />
              </div>
            </div>
          </div>
        )
      )}

      {/* Import results */}
      {importLog.length > 0 && (
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">
            Import Results
          </div>
          <div className="divide-y divide-slate-100 max-h-40 overflow-y-auto">
            {importLog.map((l, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                <span className={l.ok ? "text-green-500" : "text-red-500"}>{l.ok ? "✓" : "✗"}</span>
                <span className="truncate text-slate-700">{l.name}</span>
                {!l.ok && <span className="text-xs text-red-500 ml-auto flex-shrink-0">{l.msg}</span>}
              </div>
            ))}
          </div>
          {!importing && (
            <div className="border-t border-slate-100 px-3 py-2 text-xs text-slate-400 bg-slate-50">
              Imported files appear in the File Training list above — click Publish to Index to add them to your AI index.
            </div>
          )}
        </div>
      )}

      {/* Supported types */}
      <p className="text-xs text-slate-400">
        Supported: PDF, DOCX, DOC, XLSX, XLS, CSV, TXT, MD, PPTX, JPG, PNG
      </p>
    </div>
  );
}
